import { Card, Divider } from "@drone-js/core";
import { VStack } from "apps/commons/components/Stack";
import { ContractPropertyCard } from "apps/oneOnboarding/contract/ContractPropertyCard";
import { LicenseController } from "apps/oneOnboarding/contract/LicenseController";
import { usePropertyContext } from "apps/oneOnboarding/usePropertyContext";
import type { PropertyData } from "libs/codegen/property-onboarding";
import type { FunctionComponent } from "react";
import type { FieldArrayWithId } from "react-hook-form";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";

interface Props {
    properties: FieldArrayWithId<BulkPropertyFormData, "propertyDataList", "id">[];
}

interface PropertyItemProps {
    property: PropertyData;
    fieldPrefix: string;
}

export const PropertiesOverview: FunctionComponent<Props> = ({ properties }) => {
    return (
        <VStack gap="16">
            {properties.map((property, index) => (
                <PropertyItem
                    key={`contract-property-${property.propertyId}`}
                    property={property}
                    fieldPrefix={`propertyDataList.${index}`}
                />
            ))}
        </VStack>
    );
};

const PropertyItem: FunctionComponent<PropertyItemProps> = ({ property, fieldPrefix }) => {
    const { licenseConfiguration } = usePropertyContext(property.propertyId, property.onboardingId ?? undefined);

    const propertyCard = (
        <ContractPropertyCard
            name={property.propertyName}
            photo={property.photos?.[0]?.src}
            location={`${property.city?.name}, ${property.state?.name}`}
        />
    );

    // TODO(PRO-841): Include requiredFileUpload when the file upload flow is implemented.
    const isLicenseRequired =
        licenseConfiguration?.requiredExemptible === true || licenseConfiguration?.requiredLicenseId === true;

    return (
        <Card type="non-interactive" variant="filled" backgroundColor="base.inset" p="16">
            <VStack gap="16">
                {propertyCard}
                {licenseConfiguration && isLicenseRequired && (
                    <>
                        <Divider direction="horizontal" color="base.subtle" />
                        <LicenseController {...licenseConfiguration} fieldPrefix={fieldPrefix} />
                    </>
                )}
            </VStack>
        </Card>
    );
};
